import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak, Footer, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

// Common formatting and branding for both export types
const BRANDING_TEXT = "Created using the GospelWise Author App | gospelwiseauthor.app";

/**
 * Export project to PDF format
 * @param {Object} project - The project data from projects_storywise table
 * @param {Object} user - The user data (for author name)
 */
export const exportToPDF = (project, user) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Set default font to a serif font
  doc.setFont("times", "normal");
  
  // PDF formatting settings - ensure proper margins
  const margin = 20; // increase margin to avoid text running off the page
  const pageWidth = 210 - (margin * 2);
  let yPos = margin;

  // Add cover page
  doc.setFontSize(24);
  doc.text(project.title || "Untitled Project", 105, 80, {align: 'center'});

  doc.setFontSize(16);
  doc.text(`${project.type} Project`, 105, 100, {align: 'center'});

  if (user?.name) {
    doc.setFontSize(14);
    doc.text(`By ${user.name}`, 105, 120, {align: 'center'});
  }

  // Add branding on the cover page
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(BRANDING_TEXT, 105, 280, {align: 'center'});

  // Reset text color
  doc.setTextColor(0, 0, 0);

  // Add a new page to start content
  doc.addPage();

  // Add footer to all pages
  const addFooter = () => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(BRANDING_TEXT, 105, 287, {align: 'center'});
      doc.text(`Page ${i} of ${pageCount}`, 195, 287, {align: 'right'});
      doc.setTextColor(0, 0, 0);
    }
  };

  // Helper to add section with title
  const addSection = (title, content, options = {}) => {
    // Check if we need a page break
    if (options.pageBreak && yPos > margin) {
      doc.addPage();
      yPos = margin;
    }

    // Add section title
    const fontSize = options.isH1 ? 18 : options.isH2 ? 14 : 12;
    doc.setFontSize(fontSize);
    doc.setFont("times", "bold");
    doc.text(title, margin, yPos);
    yPos += fontSize === 18 ? 12 : fontSize === 14 ? 10 : 8;

    // Add section content
    doc.setFontSize(11);
    doc.setFont("times", "normal");
    
    if (typeof content === 'string') {
      // If content is empty, just add a line break
      if (content && content.trim()) {
        const textLines = doc.splitTextToSize(content, pageWidth - 5); // reduce width slightly for safety
        doc.text(textLines, margin, yPos);
        yPos += (textLines.length * 6);
      }
      
      // Always add spacing after section regardless of content
      yPos += 10;
    } else if (typeof content === 'object' && content) {
      Object.entries(content).forEach(([key, value]) => {
        // Add field label
        doc.setFont("times", "bold");
        doc.text(formatLabel(key) + ":", margin, yPos);
        yPos += 6;

        // Add field value if not empty
        doc.setFont("times", "normal");
        if (value && value.trim()) {
          const textLines = doc.splitTextToSize(value, pageWidth - 5); // reduce width slightly
          doc.text(textLines, margin, yPos);
          yPos += (textLines.length * 6);
        }
        
        yPos += 8; // Space after each field
      });
    }

    // Check if we need to add a new page - use lower threshold to avoid cutting off text
    if (yPos > 260) {
      doc.addPage();
      yPos = margin;
    }
  };

  // Format field labels
  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  // Export fiction content
  exportFictionToPDF(project, doc, addSection);

  // Add footers
  addFooter();

  // Save the PDF
  doc.save(`${project.title || "Project"}_StoryWise_Framework.pdf`);
};

/**
 * Export fiction project to PDF following StoryWise Framework structure
 */
const exportFictionToPDF = (project, doc, addSection) => {
  const content = project.content || {};

  // Tab 1: Story Concept (H1)
  addSection("Story Concept", "", {isH1: true, pageBreak: false});

  // Project Overview (H2)
  addSection("Project Overview", "", {isH2: true});
  addSection("What is the title of your project?", project.title);
  addSection("What genre best describes your story?", project.genre);
  addSection("Who is your target audience?", project.target_audience);
  addSection("What is your word count goal?", project.word_count_goal ? `${project.word_count_goal.toLocaleString()} words` : "");

  // One-Line Premise (H2)
  addSection("One-Line Premise", "", {isH2: true});
  addSection("Write a compelling one-sentence summary of your story that captures the essence of your plot, main character, and central conflict.", content.premise);

  // Central Theme (H2)
  addSection("Central Theme", "", {isH2: true});
  addSection("What is the central theme or message of your story? What deeper truth or life lesson do you want readers to discover?", content.theme);

  // Faith Element (H2)
  addSection("Faith Element", "", {isH2: true});
  addSection("How does faith or spiritual truth appear in your story? What biblical principles are you exploring through your characters and plot?", content.faith_element);

  // Key Story Arc (H2)
  addSection("Key Story Arc", "", {isH2: true});
  addSection("How does your story begin? What is the opening situation that draws readers in?", content.story_beginning);
  addSection("What happens in the middle of your story? What are the main challenges and conflicts?", content.story_middle);
  addSection("How does your story resolve? What is the satisfying conclusion?", content.story_end);

  // Reader Transformation (H2)
  addSection("Reader Transformation", "", {isH2: true});
  addSection("What do you want readers to think, feel, or do after reading your story? How should they be changed?", content.reader_transformation);

  // Tab 2: StoryWise Structure (H1) - New Page
  addSection("StoryWise Structure", "", {isH1: true, pageBreak: true});

  const storyStructure = content.story_structure || {};
  const movements = [
    {
      key: 'openingScene',
      title: 'Movement 1: Opening Scene',
      question: 'Describe your opening scene that establishes the hero and creates reader connection. Show their ordinary world and what makes them relatable.'
    },
    {
      key: 'hookingMoment',
      title: 'Movement 2: Hooking Moment',
      question: 'What unexpected moment or event captures the reader\'s attention and signals that something is about to change?'
    },
    {
      key: 'firstPlotPoint',
      title: 'Movement 3: First Plot Point',
      question: 'What event or decision forces your protagonist to engage with the main story problem?'
    },
    {
      key: 'firstPinchPoint',
      title: 'Movement 4: First Pinch Point',
      question: 'How does the antagonistic force (person, situation, or internal struggle) reveal its true threat?'
    },
    {
      key: 'midpointShift',
      title: 'Movement 5: Midpoint Shift',
      question: 'What major revelation, decision, or shift in perspective changes everything at the middle of your story?'
    },
    {
      key: 'secondPinchPoint',
      title: 'Movement 6: Second Pinch Point',
      question: 'How does the antagonistic force push back against the protagonist\'s progress? What setbacks occur?'
    },
    {
      key: 'secondPlotPoint',
      title: 'Movement 7: Second Plot Point',
      question: 'What final revelation, tool, or insight prepares your protagonist for the final confrontation?'
    },
    {
      key: 'finalResolution',
      title: 'Movement 8: Final Resolution',
      question: 'How does your protagonist confront and overcome (or fail to overcome) the central conflict?'
    },
    {
      key: 'worldBackToNormal',
      title: 'Movement 9: World Back to Normal',
      question: 'How has your protagonist and their world changed? What does the new normal look like?'
    }
  ];

  movements.forEach((movement, index) => {
    if (index > 0 && index % 3 === 0) {
      addSection(movement.title, "", {isH2: true, pageBreak: true});
    } else {
      addSection(movement.title, "", {isH2: true});
    }
    addSection(movement.question, storyStructure[movement.key]);
  });

  // Tab 3: Character Profiles (H1) - New Page
  addSection("Character Profiles", "", {isH1: true, pageBreak: true});

  // Export detailed character profiles
  const characters = content.characters || [];
  if (characters.length > 0) {
    characters.forEach((character, index) => {
      // Add page break before each character (except the first one)
      if (index > 0) {
        // Force a page break between characters
        addSection(`${character.name || 'Unnamed Character'} (${getRoleDisplayName(character.role)})`, "", {isH1: true, pageBreak: true});
      } else {
        addSection(`${character.name || 'Unnamed Character'} (${getRoleDisplayName(character.role)})`, "", {isH1: true});
      }

      // Basic Information
      addSection("Basic Information", "", {isH2: true});
      addSection("Character Name", character.name || "");
      addSection("Role", getRoleDisplayName(character.role));
      addSection("Age", character.age || "");
      addSection("Occupation", character.occupation || "");
      addSection("Brief Description", character.description || "");

      // Physical Description
      addSection("Physical Description", "", {isH2: true});
      addSection("Physical Appearance", character.appearance || "");
      addSection("Mannerisms & Habits", character.mannerisms || "");

      // Personality & Psychology
      addSection("Personality & Psychology", "", {isH2: true});
      addSection("Personality Overview", character.personality || "");
      addSection("Strengths & Virtues", character.strengths || "");
      addSection("Weaknesses & Flaws", character.weaknesses || "");
      addSection("Fears & Insecurities", character.fears || "");

      // Background & History
      addSection("Background & History", "", {isH2: true});
      addSection("Background & History", character.background || "");
      addSection("Secrets & Hidden Elements", character.secrets || "");

      // Goals & Motivation
      addSection("Goals & Motivation", "", {isH2: true});
      addSection("Core Motivation", character.motivation || "");
      addSection("Goals & Desires", character.goals || "");
      addSection("Character Arc & Growth", character.character_arc || "");

      // Relationships & Voice
      addSection("Relationships & Voice", "", {isH2: true});
      addSection("Key Relationships", character.relationships || "");
      addSection("Dialogue Voice & Speech Patterns", character.dialogue_voice || "");

      // Spiritual Journey
      addSection("Spiritual Journey", "", {isH2: true});
      addSection("Spiritual Journey & Faith", character.spiritual_journey || "");
    });
  } else {
    // Fall back to legacy character notes if no detailed profiles exist
    addSection("Quick Character Notes", "", {isH2: true});

    // Protagonist (H2)
    addSection("Protagonist", "", {isH2: true});
    addSection("Describe your protagonist's name, age, background, and key characteristics. What makes them unique and relatable?", content.protagonist);
    addSection("What does your protagonist want? What drives them throughout the story?", content.protagonist_goals);
    addSection("What are their weaknesses or flaws? How will they grow and change throughout the story?", content.protagonist_flaw);

    // Antagonist (H2)
    addSection("Antagonist", "", {isH2: true});
    addSection("Describe your antagonist (person, force, or internal struggle). What makes them a compelling opponent?", content.antagonist);
    addSection("What drives the antagonist? Why do they oppose your protagonist? What are their motivations?", content.antagonist_motivations);

    // Supporting Characters (H2)
    addSection("Supporting Characters", "", {isH2: true});
    addSection("Describe key supporting characters, their roles, and how they impact the story. Include allies, mentors, and other important figures.", content.supporting_characters);
  }

  // Tab 4: Story World (H1) - New Page
  addSection("Story World", "", {isH1: true, pageBreak: true});

  // Setting & Time Period (H2)
  addSection("Setting & Time Period", "", {isH2: true});
  addSection("Describe where your story takes place. Include physical details, atmosphere, and mood of the primary locations.", content.setting);
  addSection("When does your story take place? What historical or cultural context is important to your story?", content.time_period);

  // World Rules & Logic (H2)
  addSection("World Rules & Logic", "", {isH2: true});
  addSection("What are the rules of your story world? Include spiritual elements, social structures, or any unique aspects that govern how your world works.", content.world_rules);

  // Central Conflict & Stakes (H2)
  addSection("Central Conflict & Stakes", "", {isH2: true});
  addSection("What is the central conflict driving your story? What problem must be solved or challenge must be overcome?", content.conflict);
  addSection("What will happen if your protagonist fails? What are they fighting for? Why should readers care?", content.stakes);

  // Faith & Spiritual Elements (H2)
  addSection("Faith & Spiritual Elements", "", {isH2: true});
  addSection("How does faith play a role in your story world? What spiritual themes or elements are woven throughout the narrative?", content.spiritual_elements);
};

/**
 * Helper function to get display name for character roles
 */
const getRoleDisplayName = (role) => {
  switch (role) {
    case 'protagonist': return 'Protagonist';
    case 'antagonist': return 'Antagonist';
    case 'supporting': return 'Supporting Character';
    default: return 'Character';
  }
};

/**
 * Export project to Word document format
 * @param {Object} project - The project data from projects_storywise table
 * @param {Object} user - The user data (for author name)
 */
export const exportToWord = async (project, user) => {
  try {
    // Create a new document
    const doc = new Document({
      creator: user?.name || "GospelWise Author",
      title: project.title || "Untitled Project",
      description: `${project.type} project export from GospelWise Author App`,
      sections: [],
    });

    // Create document sections - Cover Page
    const coverPageChildren = [];

    // Add cover page
    coverPageChildren.push(
      new Paragraph({
        text: project.title || "Untitled Project",
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: {after: 400},
      })
    );

    coverPageChildren.push(
      new Paragraph({
        text: `${project.type} Project - StoryWise Framework`,
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: {after: 400},
      })
    );

    if (user?.name) {
      coverPageChildren.push(
        new Paragraph({
          text: `By ${user.name}`,
          heading: HeadingLevel.HEADING_2,
          alignment: AlignmentType.CENTER,
          spacing: {after: 800},
        })
      );
    }

    // Add cover page section
    doc.addSection({
      children: coverPageChildren,
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              text: BRANDING_TEXT,
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
      },
    });

    // Helper to add question and answer
    const addQuestionAnswer = (children, question, answer) => {
      // Add question in italic
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: question,
              italics: true,
            }),
          ],
          spacing: {before: 200, after: 100},
        })
      );

      // Add answer only if not empty
      if (answer && answer.trim()) {
        children.push(
          new Paragraph({
            text: answer,
            spacing: {after: 300},
          })
        );
      } else {
        // Just add spacing paragraph
        children.push(
          new Paragraph({
            text: "",
            spacing: {after: 200},
          })
        );
      }
    };

    // Format field labels
    const formatLabel = (key) => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
    };

    // Section 1: Story Concept
    const storyConceptChildren = [];
    storyConceptChildren.push(
      new Paragraph({
        text: "Story Concept",
        heading: HeadingLevel.HEADING_1,
        spacing: {after: 400},
      })
    );

    const content = project.content || {};

    // Project Overview
    storyConceptChildren.push(
      new Paragraph({
        text: "Project Overview",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "What is the title of your project?", project.title);
    addQuestionAnswer(storyConceptChildren, "What genre best describes your story?", project.genre);
    addQuestionAnswer(storyConceptChildren, "Who is your target audience?", project.target_audience);
    addQuestionAnswer(storyConceptChildren, "What is your word count goal?", project.word_count_goal ? `${project.word_count_goal.toLocaleString()} words` : "");

    // One-Line Premise
    storyConceptChildren.push(
      new Paragraph({
        text: "One-Line Premise",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "Write a compelling one-sentence summary of your story that captures the essence of your plot, main character, and central conflict.", content.premise);

    // Central Theme
    storyConceptChildren.push(
      new Paragraph({
        text: "Central Theme",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "What is the central theme or message of your story? What deeper truth or life lesson do you want readers to discover?", content.theme);

    // Faith Element
    storyConceptChildren.push(
      new Paragraph({
        text: "Faith Element",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "How does faith or spiritual truth appear in your story? What biblical principles are you exploring through your characters and plot?", content.faith_element);

    // Key Story Arc
    storyConceptChildren.push(
      new Paragraph({
        text: "Key Story Arc",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "How does your story begin? What is the opening situation that draws readers in?", content.story_beginning);
    addQuestionAnswer(storyConceptChildren, "What happens in the middle of your story? What are the main challenges and conflicts?", content.story_middle);
    addQuestionAnswer(storyConceptChildren, "How does your story resolve? What is the satisfying conclusion?", content.story_end);

    // Reader Transformation
    storyConceptChildren.push(
      new Paragraph({
        text: "Reader Transformation",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyConceptChildren, "What do you want readers to think, feel, or do after reading your story? How should they be changed?", content.reader_transformation);

    // Add Story Concept section
    doc.addSection({
      children: storyConceptChildren,
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              text: BRANDING_TEXT,
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
      },
    });

    // Section 2: StoryWise Structure
    const structureChildren = [];
    structureChildren.push(
      new Paragraph({
        text: "StoryWise Structure",
        heading: HeadingLevel.HEADING_1,
        spacing: {after: 400},
      })
    );

    const storyStructure = content.story_structure || {};
    const movements = [
      {
        key: 'openingScene',
        title: 'Movement 1: Opening Scene',
        question: 'Describe your opening scene that establishes the hero and creates reader connection. Show their ordinary world and what makes them relatable.'
      },
      {
        key: 'hookingMoment',
        title: 'Movement 2: Hooking Moment',
        question: 'What unexpected moment or event captures the reader\'s attention and signals that something is about to change?'
      },
      {
        key: 'firstPlotPoint',
        title: 'Movement 3: First Plot Point',
        question: 'What event or decision forces your protagonist to engage with the main story problem?'
      },
      {
        key: 'firstPinchPoint',
        title: 'Movement 4: First Pinch Point',
        question: 'How does the antagonistic force (person, situation, or internal struggle) reveal its true threat?'
      },
      {
        key: 'midpointShift',
        title: 'Movement 5: Midpoint Shift',
        question: 'What major revelation, decision, or shift in perspective changes everything at the middle of your story?'
      },
      {
        key: 'secondPinchPoint',
        title: 'Movement 6: Second Pinch Point',
        question: 'How does the antagonistic force push back against the protagonist\'s progress? What setbacks occur?'
      },
      {
        key: 'secondPlotPoint',
        title: 'Movement 7: Second Plot Point',
        question: 'What final revelation, tool, or insight prepares your protagonist for the final confrontation?'
      },
      {
        key: 'finalResolution',
        title: 'Movement 8: Final Resolution',
        question: 'How does your protagonist confront and overcome (or fail to overcome) the central conflict?'
      },
      {
        key: 'worldBackToNormal',
        title: 'Movement 9: World Back to Normal',
        question: 'How has your protagonist and their world changed? What does the new normal look like?'
      }
    ];

    movements.forEach((movement, index) => {
      structureChildren.push(
        new Paragraph({
          text: movement.title,
          heading: HeadingLevel.HEADING_2,
          spacing: {after: 300},
        })
      );
      
      addQuestionAnswer(structureChildren, movement.question, storyStructure[movement.key]);

      // Add page breaks after every three movements (except at the end)
      if ((index + 1) % 3 === 0 && index < movements.length - 1) {
        structureChildren.push(new Paragraph({
          text: "",
          pageBreak: true
        }));
      }
    });

    // Add StoryWise Structure section
    doc.addSection({
      children: structureChildren,
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              text: BRANDING_TEXT,
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
      },
    });

    // Section 3: Character Profiles
    // Export detailed character profiles
    const characters = content.characters || [];
    
    if (characters.length > 0) {
      // Create a separate section for each character
      characters.forEach((character, index) => {
        // Create a new array of children elements for this character's section
        const characterChildren = [];
        
        // Character name and role as H1
        characterChildren.push(
          new Paragraph({
            text: "Character Profiles",
            heading: HeadingLevel.HEADING_1,
            spacing: {after: 200},
          })
        );
        
        characterChildren.push(
          new Paragraph({
            text: `${character.name || 'Unnamed Character'} (${getRoleDisplayName(character.role)})`,
            heading: HeadingLevel.HEADING_1,
            spacing: {after: 400},
          })
        );

        // Basic Information
        characterChildren.push(
          new Paragraph({
            text: "Basic Information",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Character Name", character.name || "");
        addQuestionAnswer(characterChildren, "Role", getRoleDisplayName(character.role));
        addQuestionAnswer(characterChildren, "Age", character.age || "");
        addQuestionAnswer(characterChildren, "Occupation", character.occupation || "");
        addQuestionAnswer(characterChildren, "Brief Description", character.description || "");

        // Physical Description
        characterChildren.push(
          new Paragraph({
            text: "Physical Description",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Physical Appearance", character.appearance || "");
        addQuestionAnswer(characterChildren, "Mannerisms & Habits", character.mannerisms || "");

        // Personality & Psychology
        characterChildren.push(
          new Paragraph({
            text: "Personality & Psychology",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Personality Overview", character.personality || "");
        addQuestionAnswer(characterChildren, "Strengths & Virtues", character.strengths || "");
        addQuestionAnswer(characterChildren, "Weaknesses & Flaws", character.weaknesses || "");
        addQuestionAnswer(characterChildren, "Fears & Insecurities", character.fears || "");

        // Background & History
        characterChildren.push(
          new Paragraph({
            text: "Background & History",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Background & History", character.background || "");
        addQuestionAnswer(characterChildren, "Secrets & Hidden Elements", character.secrets || "");

        // Goals & Motivation
        characterChildren.push(
          new Paragraph({
            text: "Goals & Motivation",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Core Motivation", character.motivation || "");
        addQuestionAnswer(characterChildren, "Goals & Desires", character.goals || "");
        addQuestionAnswer(characterChildren, "Character Arc & Growth", character.character_arc || "");

        // Relationships & Voice
        characterChildren.push(
          new Paragraph({
            text: "Relationships & Voice",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Key Relationships", character.relationships || "");
        addQuestionAnswer(characterChildren, "Dialogue Voice & Speech Patterns", character.dialogue_voice || "");

        // Spiritual Journey
        characterChildren.push(
          new Paragraph({
            text: "Spiritual Journey",
            heading: HeadingLevel.HEADING_2,
            spacing: {after: 300},
          })
        );
        addQuestionAnswer(characterChildren, "Spiritual Journey & Faith", character.spiritual_journey || "");

        // Add each character as a separate section with its own page
        doc.addSection({
          children: characterChildren,
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  text: BRANDING_TEXT,
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          },
        });
      });
    } else {
      // Fall back to legacy character notes if no detailed profiles exist
      const charactersChildren = [];
      
      charactersChildren.push(
        new Paragraph({
          text: "Character Profiles",
          heading: HeadingLevel.HEADING_1,
          spacing: {after: 400},
        })
      );

      charactersChildren.push(
        new Paragraph({
          text: "Quick Character Notes",
          heading: HeadingLevel.HEADING_2,
          spacing: {after: 300},
        })
      );

      // Protagonist
      charactersChildren.push(
        new Paragraph({
          text: "Protagonist",
          heading: HeadingLevel.HEADING_2,
          spacing: {after: 300},
        })
      );
      addQuestionAnswer(charactersChildren, "Describe your protagonist's name, age, background, and key characteristics. What makes them unique and relatable?", content.protagonist);
      addQuestionAnswer(charactersChildren, "What does your protagonist want? What drives them throughout the story?", content.protagonist_goals);
      addQuestionAnswer(charactersChildren, "What are their weaknesses or flaws? How will they grow and change throughout the story?", content.protagonist_flaw);

      // Antagonist
      charactersChildren.push(
        new Paragraph({
          text: "Antagonist",
          heading: HeadingLevel.HEADING_2,
          spacing: {after: 300},
        })
      );
      addQuestionAnswer(charactersChildren, "Describe your antagonist (person, force, or internal struggle). What makes them a compelling opponent?", content.antagonist);
      addQuestionAnswer(charactersChildren, "What drives the antagonist? Why do they oppose your protagonist? What are their motivations?", content.antagonist_motivations);

      // Supporting Characters
      charactersChildren.push(
        new Paragraph({
          text: "Supporting Characters",
          heading: HeadingLevel.HEADING_2,
          spacing: {after: 300},
        })
      );
      addQuestionAnswer(charactersChildren, "Describe key supporting characters, their roles, and how they impact the story. Include allies, mentors, and other important figures.", content.supporting_characters);
      
      // Add Characters section
      doc.addSection({
        children: charactersChildren,
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                text: BRANDING_TEXT,
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
      });
    }

    // Section 4: Story World
    const storyWorldChildren = [];
    storyWorldChildren.push(
      new Paragraph({
        text: "Story World",
        heading: HeadingLevel.HEADING_1,
        spacing: {after: 400},
      })
    );

    // Setting & Time Period
    storyWorldChildren.push(
      new Paragraph({
        text: "Setting & Time Period",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyWorldChildren, "Describe where your story takes place. Include physical details, atmosphere, and mood of the primary locations.", content.setting);
    addQuestionAnswer(storyWorldChildren, "When does your story take place? What historical or cultural context is important to your story?", content.time_period);

    // World Rules & Logic
    storyWorldChildren.push(
      new Paragraph({
        text: "World Rules & Logic",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyWorldChildren, "What are the rules of your story world? Include spiritual elements, social structures, or any unique aspects that govern how your world works.", content.world_rules);

    // Central Conflict & Stakes
    storyWorldChildren.push(
      new Paragraph({
        text: "Central Conflict & Stakes",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyWorldChildren, "What is the central conflict driving your story? What problem must be solved or challenge must be overcome?", content.conflict);
    addQuestionAnswer(storyWorldChildren, "What will happen if your protagonist fails? What are they fighting for? Why should readers care?", content.stakes);

    // Faith & Spiritual Elements
    storyWorldChildren.push(
      new Paragraph({
        text: "Faith & Spiritual Elements",
        heading: HeadingLevel.HEADING_2,
        spacing: {after: 300},
      })
    );
    addQuestionAnswer(storyWorldChildren, "How does faith play a role in your story world? What spiritual themes or elements are woven throughout the narrative?", content.spiritual_elements);

    // Add Story World section
    doc.addSection({
      children: storyWorldChildren,
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              text: BRANDING_TEXT,
              alignment: AlignmentType.CENTER,
            }),
          ],
        }),
      },
    });

    // Generate the document
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${project.title || "Project"}_StoryWise_Framework.docx`);
    return true;
  } catch (error) {
    console.error("Error generating Word document:", error);
    throw new Error("Failed to generate Word document");
  }
};